import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMatrixParams } from '@/app/MatrixSwitch';
import type {
  AccountTypeOption,
  ChartTemplateAccountRow,
  SaveChartTemplateAccountPayload,
} from '@/core/models/firm-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { firmSettingsService } from '@/core/services/firmSettings';
import {
  ACCOUNT_CODE_MAX_LENGTH,
  accountCodeProblem,
  accountTypeOffered,
  accountTypesFor,
} from '@/core/utils/chartAccount';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { Select, SelectOption } from '@/kit/Select';
import { SideList } from '@/kit/SideList';

const COMPANY_TYPES: { id: number; label: string }[] = [
  { id: 0, label: 'Limited' },
  { id: 1, label: 'Sole Trader' },
  { id: 2, label: 'Partnership' },
  { id: 3, label: 'LLP' },
];

const ALL_COMPANY_TYPES = [0, 1, 2, 3];

interface AccountFormValue {
  companyTypes: number[];
  accountType: number | null;
  accountName: string;
  accountCode: string;
  vatCode: string | null;
  isActive: boolean;
}

const BLANK: AccountFormValue = {
  companyTypes: ALL_COMPANY_TYPES,
  accountType: null,
  accountName: '',
  accountCode: '',
  vatCode: '',
  isActive: true,
};

type Field = 'accountType' | 'accountName' | 'accountCode';

/** BUG-018: the old application's message for a missing or wrong Add Account field. */
function fieldMessage(field: Field, value: AccountFormValue): string {
  switch (field) {
    case 'accountType':
      return 'Please select account type';
    case 'accountName':
      return 'Please provide account name';
    default:
      return value.accountCode.trim().length === 0
        ? 'Please provide account code'
        : (accountCodeProblem(value.accountCode.trim()) ?? 'Please provide account code');
  }
}

function fieldIsInvalid(field: Field, value: AccountFormValue): boolean {
  switch (field) {
    case 'accountType':
      return value.accountType === null || value.accountType === undefined || (value.accountType as unknown) === '';
    case 'accountName':
      return value.accountName.trim().length === 0 || value.accountName.length > 100;
    case 'accountCode':
      return value.accountCode.trim().length === 0 || accountCodeProblem(value.accountCode.trim()) !== null;
  }
}

const returnTypeOf = (raw: string | undefined): number => {
  const type = Number(raw ?? null);
  return Number.isInteger(type) && type >= 0 && type <= 3 ? type : 0;
};

/**
 * Angular AddNewAccountComponent: create/edit a default chart-of-accounts template account,
 * `/home/settings/addnewaccount` (add, optional `;type=0..3`) and `;id=<base64>;type=N` (edit).
 */
export function AddNewAccount() {
  const params = useMatrixParams();
  const navigate = useNavigate();

  const [form, setForm] = useState<AccountFormValue>(BLANK);
  const [touched, setTouched] = useState<ReadonlySet<Field>>(() => new Set());
  const [accountTypes, setAccountTypes] = useState<AccountTypeOption[]>([]);
  const [vatCodes, setVatCodes] = useState<string[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  /** True while a save is in flight, so a second click on Save / Save and Exit cannot submit twice. */
  const [saving, setSaving] = useState(false);

  const accountId = useRef<string | null>(null);
  /** The account type an existing account was saved with: it stays on offer even where the rule would not offer it. */
  const [savedAccountType, setSavedAccountType] = useState<number | null>(null);
  const isArchived = useRef(false);
  const returnType = useRef(0);
  const mounted = useRef(true);

  const startNew = (companyTypes: number[]) => {
    setIsEdit(false);
    accountId.current = null;
    setSavedAccountType(null);
    isArchived.current = false;
    setTouched(new Set());
    setForm({ ...BLANK, companyTypes });
  };

  const populate = (row: ChartTemplateAccountRow) => {
    setIsEdit(true);
    accountId.current = row.id;
    setSavedAccountType(row.accountTypeId);
    isArchived.current = row.isArchived;
    returnType.current = row.businessType;
    setTouched(new Set());
    // An existing account belongs to one business type's chart (the company type checkbox is disabled).
    setForm({
      companyTypes: [row.businessType],
      accountType: row.accountTypeId,
      accountName: row.accountName,
      accountCode: row.code,
      vatCode: row.vatCode ?? '',
      isActive: row.isActive,
    });
  };

  const load = (id: string) => {
    firmSettingsService
      .getChartTemplateAccount(id)
      .then((row) => {
        if (mounted.current) populate(row);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      })
      .finally(() => {
        if (mounted.current) setSaving(false);
      });
  };

  useEffect(() => {
    mounted.current = true;
    firmSettingsService
      .getChartTemplateOptions()
      .then((options) => {
        if (!mounted.current) return;
        setAccountTypes(options.accountTypes);
        setVatCodes(options.vatCodes);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  // Angular subscribed to paramMap: every change of the matrix params reloads / restarts the form.
  const encoded = params.id;
  const type = params.type;
  const hasType = Object.hasOwn(params, 'type');
  useEffect(() => {
    returnType.current = returnTypeOf(type);
    if (encoded) {
      let id: string;
      try {
        id = atob(encoded);
      } catch {
        return;
      }
      load(id);
    } else {
      startNew(hasType ? [returnType.current] : ALL_COMPANY_TYPES);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encoded, type, hasType]);

  const onClose = () => void navigate(matrixPath('/home/settings', 'chart-accounts', { type: returnType.current }));

  const save = (exitAfterSave: boolean) => {
    const invalidFields = (['accountType', 'accountName', 'accountCode'] as Field[]).filter((field) =>
      fieldIsInvalid(field, form),
    );
    if (form.companyTypes.length === 0) {
      notifyError('Please select at least one company type.');
      return;
    }
    if (invalidFields.length > 0) {
      setTouched(new Set(['accountType', 'accountName', 'accountCode']));
      // BUG-018: as the old application, a message for each missing or wrong field (the ones shown under each field),
      // not one general "Please fill all the mandatory fields." One toast shows at a time, so it names them all.
      notifyError(invalidFields.map((field) => `${fieldMessage(field, form)}.`).join(' '));
      return;
    }

    const businessTypes = form.companyTypes;
    const payload: SaveChartTemplateAccountPayload = {
      id: accountId.current,
      businessTypes,
      accountTypeId: Number(form.accountType),
      accountName: (form.accountName ?? '').trim(),
      accountCode: (form.accountCode ?? '').trim(),
      vatCode: form.vatCode ? form.vatCode : null,
      isActive: !!form.isActive,
      isArchived: isArchived.current,
    };

    setSaving(true);
    firmSettingsService
      .saveChartTemplateAccount(payload)
      .then((rows) => {
        if (!mounted.current) return;
        notifySuccess(
          isEdit
            ? 'Account updated successfully'
            : rows.length > 1
              ? `Account added to ${rows.length} charts of accounts`
              : 'Account added successfully',
        );
        if (businessTypes.length === 1) {
          returnType.current = businessTypes[0];
        }
        if (exitAfterSave) {
          onClose();
        } else if (isEdit) {
          populate(rows[0]);
        } else if (rows.length === 1) {
          void navigate(
            matrixPath('/home/settings', 'addnewaccount', { id: btoa(rows[0].id), type: rows[0].businessType }),
          );
        } else {
          startNew(form.companyTypes);
        }
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const onCancel = () => {
    if (isEdit && accountId.current) {
      load(accountId.current);
    } else {
      startNew(form.companyTypes);
    }
  };

  const touch = (field: Field) => setTouched((current) => (current.has(field) ? current : new Set(current).add(field)));
  const invalid = (field: Field) => touched.has(field) && fieldIsInvalid(field, form);
  const set = <K extends keyof AccountFormValue>(key: K, value: AccountFormValue[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  /**
   * BUG-018: Partners Emoluments is for a partnership or an LLP only, so the list offers it only while every ticked company
   * type may use it, and a chosen type that the new selection no longer allows is cleared rather than sent to be refused.
   */
  const offeredAccountTypes = accountTypesFor(accountTypes, form.companyTypes, savedAccountType);
  const setCompanyTypes = (companyTypes: number[]) =>
    setForm((current) => {
      const chosen = accountTypes.find((type) => type.id === current.accountType);
      const keep = !chosen || chosen.id === savedAccountType || accountTypeOffered(chosen, companyTypes);
      return { ...current, companyTypes, accountType: keep ? current.accountType : null };
    });

  return (
    <div className="sidemenu-hide">
      <div className="d-flex h-100">
        <SideList />
        <div className="form wp-100">
          <div>
            <div className="sidenav-details mb-4">
              {/* Design review 2 Nov 2026: Save / Save and Exit / Cancel Changes are page actions, so they sit top-right in the sticky
                  header; the footer bar is gone. */}
              <PageHeader title={<span className="d-flex align-items-center">
                  <Button className="back-btn back-arrow mr-1" aria-label="back" title="Back" onClick={onClose}>
                      <Icon name="arrow_back" />
                    </Button>
                  {isEdit ? 'Edit Account' : 'Add New Account'}
                </span>}>
                <Button className="action-button primary-button" busy={saving} onClick={() => save(false)}>
                  Save
                </Button>
                <Button className="action-button primary-button" busy={saving} onClick={() => save(true)}>
                  Save and Exit
                </Button>
                <Button className="action-button secondary-button" onClick={onCancel}>
                  Cancel Changes
                </Button>
                <Button className="close-btn" aria-label="Close" onClick={onClose}>
                  <Icon name="close" />
                </Button>
              </PageHeader>
              <div className="mt-1">
                <div className="sidenav-body">
                  <form onSubmit={(event: FormEvent) => event.preventDefault()}>
                    <div className="sidenav-body">
                      <div className="sidenav-body-content p-0">
                        <div className="form-field">
                          <p>Company Type</p>
                          <div className="d-flex align-center gap-15">
                            <Checkbox
                              checked={form.companyTypes.length === 4}
                              disabled={isEdit}
                              onChange={(event) => setCompanyTypes(event.target.checked ? [0, 1, 2, 3] : [])}
                            >
                              All
                            </Checkbox>
                            {COMPANY_TYPES.map(({ id, label }) => (
                              <Checkbox
                                key={id}
                                checked={form.companyTypes.includes(id)}
                                disabled={isEdit}
                                onChange={(event) => {
                                  const next = event.target.checked
                                    ? [...form.companyTypes, id].sort()
                                    : form.companyTypes.filter((t) => t !== id);
                                  setCompanyTypes(next);
                                }}
                              >
                                {label}
                              </Checkbox>
                            ))}
                          </div>
                        </div>
                        <div className="form-field">
                          <p>
                            Account Type<span className="text-red">*</span>
                          </p>
                          <Select
                            className={`w-300${invalid('accountType') ? ' ng-invalid ng-touched' : ''}`}
                            value={form.accountType}
                            onChange={(value) => set('accountType', value as number | null)}
                            onBlur={() => touch('accountType')}
                          >
                            {offeredAccountTypes.map((accountType) => (
                              <SelectOption key={accountType.id} value={accountType.id}>
                                {accountType.name} ({accountType.groupName})
                              </SelectOption>
                            ))}
                          </Select>
                          {invalid('accountType') && (
                            <div className="text-red font-size-12 mt-1">Please select account type</div>
                          )}
                        </div>
                        <div className="form-field">
                          <p>
                            Account Name<span className="text-red">*</span>
                          </p>
                          <InputField
                            fieldClassName="w-300"
                            data-control="accountName"
                            value={form.accountName}
                            onChange={(event) => set('accountName', event.target.value)}
                            onBlur={() => touch('accountName')}
                            invalid={invalid('accountName')}
                            maxLength={100}
                          />
                          {invalid('accountName') && (
                            <div className="text-red font-size-12 mt-1">Please provide account name</div>
                          )}
                        </div>
                        <div className="form-field">
                          <p>
                            Account Code<span className="text-red">*</span>
                          </p>
                          <InputField
                            fieldClassName="w-300"
                            data-control="accountCode"
                            value={form.accountCode}
                            onChange={(event) => set('accountCode', event.target.value)}
                            onBlur={() => touch('accountCode')}
                            invalid={invalid('accountCode')}
                            maxLength={ACCOUNT_CODE_MAX_LENGTH}
                          />
                          {invalid('accountCode') && (
                            <div className="text-red font-size-12 mt-1">
                              {form.accountCode.trim().length === 0
                                ? 'Please provide account code'
                                : accountCodeProblem(form.accountCode.trim())}
                            </div>
                          )}
                        </div>
                        <div className="form-field">
                          <p>VAT Code</p>
                          <Select
                            className="w-300"
                            value={form.vatCode}
                            onChange={(value) => set('vatCode', value as string | null)}
                          >
                            <SelectOption value="">Select</SelectOption>
                            {vatCodes.map((code) => (
                              <SelectOption key={code} value={code}>
                                {code}
                              </SelectOption>
                            ))}
                          </Select>
                        </div>
                        <Checkbox checked={form.isActive} onChange={(event) => set('isActive', event.target.checked)}>
                          Active
                        </Checkbox>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
